import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useWallet } from '../components/WalletContext';
import { web } from '@klever/sdk-web';
import axios from 'axios';
import {
  FaLayerGroup,
  FaCheckCircle,
  FaTimesCircle,
  FaTag,
  FaPaperPlane,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaCoins,
  FaSpinner,
  FaImage,
  FaTimes,
  FaCube,
  FaPlus,
  FaMinus
} from 'react-icons/fa';
import AuctionListingModal from '../components/AuctionListingModal';
import { isSFT, isNFT, formatSupplyInfo, calculateRarity, getRarityColor } from '../utils/sftUtils';
import '../styles/AssetDetailsPage.css';

const AssetDetailsPage = () => {
  const { assetId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { walletAddress, useKdaFee, selectedKda } = useWallet();

  const [assets, setAssets] = useState([]);
  const [assetDetails, setAssetDetails] = useState({});
  const [page, setPage] = useState(
    parseInt(new URLSearchParams(location.search).get('page')) || 1
  );
  const [totalPages, setTotalPages] = useState(0);
  const [listingPrice, setListingPrice] = useState('');
  const [endDate, setEndDate] = useState('');
  const [transactionStatus, setTransactionStatus] = useState('');
  const [transactionState, setTransactionState] = useState(''); // 'processing', 'success', 'error'
  const [isSellModalOpen, setIsSellModalOpen] = useState(false);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [isAuctionModalOpen, setIsAuctionModalOpen] = useState(false);
  const [currentNFT, setCurrentNFT] = useState(null);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [sendToAddress, setSendToAddress] = useState('');
  const [isTopChoice, setIsTopChoice] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sftQuantities, setSftQuantities] = useState({}); // Track quantity per selected SFT
  const [collectionInfo, setCollectionInfo] = useState(null); // Store collection-level info

  const toggleSelectAsset = (asset) => {
    setSelectedAssets((prev) =>
      prev.includes(asset) ? prev.filter((a) => a !== asset) : [...prev, asset]
    );
  };

  const toggleSelectAll = () => {
    setSelectedAssets(selectedAssets.length === assets.length ? [] : assets);
  };

  const openSellModal = () => {
    if (selectedAssets.length > 0) {
      setIsSellModalOpen(true);
      setTransactionStatus('');
      setTransactionState('');
    }
  };

  const closeSellModal = () => {
    setIsSellModalOpen(false);
    setTransactionStatus('');
    setTransactionState('');
  };

  const openSendModal = () => {
    if (selectedAssets.length > 0) {
      // Initialize quantities for SFTs
      const initialQuantities = {};
      selectedAssets.forEach(asset => {
        const details = assetDetails[asset.collectionNonceId];
        const isSftAsset = details?.isSFT || asset.balance > 1;
        if (isSftAsset) {
          initialQuantities[asset.collectionNonceId] = 1; // Start with 1
        }
      });
      setSftQuantities(initialQuantities);
      setIsSendModalOpen(true);
      setTransactionStatus('');
      setTransactionState('');
    }
  };

  const closeSendModal = () => {
    setIsSendModalOpen(false);
    setTransactionStatus('');
    setTransactionState('');
    setSendToAddress('');
    setSftQuantities({});
  };

  const fetchMetadata = async (collectionId, nftNonce) => {
    try {
      let matchingAsset = null;
      const limit = 100;
      const maxPages = 5;
      for (let p = 1; p <= maxPages; p++) {
        const url = `https://api.mainnet.klever.org/v1.0/assets/list?page=${p}&limit=${limit}`;
        const response = await axios.get(url);
        const assetList = response.data.data.assets;
        matchingAsset = assetList.find((asset) => asset.assetId === collectionId);
        if (matchingAsset) break;
      }
      if (!matchingAsset) return null;

      const metadataObject = matchingAsset.uris.find(
        (uri) => uri.key.toLowerCase() === 'metadata'
      );
      let metadataUri = metadataObject ? metadataObject.value : null;
      if (metadataUri === "QmNaa2KQ6NkjjESpPHEnAow9hivnsAkq2Gd6R26cHG28Er") {
        metadataUri = `https://ipfs.io/ipfs/${metadataUri}/${nftNonce}.json`;
      } else if (metadataUri) {
        if (metadataUri.includes("klever-mint.mypinata.cloud")) {
          metadataUri = metadataUri.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
        }
        metadataUri = `${metadataUri}/${nftNonce}`;
      }
      if (!metadataUri) return null;

      try {
        const { data: metadata } = await axios.get(metadataUri);
        if (metadata.image && metadata.image.includes("www.lovemonsternft.com")) {
          const imageName = metadata.image.split('/').pop();
          metadata.image = `https://ipfs.io/ipfs/QmWVmUDPBeQzv6fG93JxQxFVee8b6smFD3RQosQXJHiZTJ/${imageName}`;
        } else if (metadata.image && metadata.image.includes("klever-mint.mypinata.cloud")) {
          metadata.image = metadata.image.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
        }
        return metadata;
      } catch (error) {
        console.error('Primary fetch failed for', nftNonce, ', trying fallback.');
        const fallbackUri = `https://${metadataUri}.ipfs.w3s.link/${nftNonce}`;
        const { data: fallbackData } = await axios.get(fallbackUri);
        return fallbackData;
      }
    } catch (error) {
      console.error(`Error fetching metadata for asset ${nftNonce}:`, error);
      return null;
    }
  };

  const renderMetadataAttributes = (metadata) => {
    if (metadata?.attributes && metadata.attributes.length > 0) {
      return (
        <div className="metadata-attributes">
          {metadata.attributes.slice(0, 3).map((attr, idx) => (
            <div key={idx} className="metadata-attribute">
              <span className="attr-type">{attr.trait_type}</span>
              <span className="attr-value">{attr.value}</span>
            </div>
          ))}
          {metadata.attributes.length > 3 && (
            <div className="more-attributes">
              +{metadata.attributes.length - 3} more
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const url = `https://api.mainnet.klever.org/v1.0/address/${walletAddress}/collection/${assetId}?limit=20&page=${page}`;
      const response = await axios.get(url);

      const collectionData = response.data.data.collection || [];

      // Map assets and include balance information for SFTs
      const mapped = collectionData.map((asset) => ({
        collectionNonceId: asset.assetId,
        collection: asset.collection,
        nftNonce: asset.nftNonce,
        balance: asset.balance || 1, // SFTs have balance > 1, NFTs default to 1
        assetType: asset.assetType // Will be 'NFT' or 'SFT'
      }));

      setAssets(mapped);
      setTotalPages(response.data.pagination.totalPages);
      setPage(response.data.pagination.self);

      // Fetch full asset details for each (includes supply info for SFTs)
      mapped.forEach(async (asset) => {
        try {
          // Fetch full asset data from API
          const assetResponse = await axios.get(
            `https://api.mainnet.klever.org/v1.0/assets/${asset.collectionNonceId}`
          );
          const fullAssetData = assetResponse.data?.data?.asset;

          // Fetch metadata
          const metadata = await fetchMetadata(asset.collection, asset.nftNonce);

          setAssetDetails((prev) => ({
            ...prev,
            [asset.collectionNonceId]: {
              ...metadata,
              fullAssetData, // Include full API response for SFT info
              isSFT: fullAssetData && isSFT(fullAssetData),
              isNFT: fullAssetData && isNFT(fullAssetData),
              supplyInfo: fullAssetData ? formatSupplyInfo(fullAssetData) : null,
              rarity: fullAssetData ? calculateRarity(fullAssetData) : null
            },
          }));
        } catch (err) {
          console.error(`Error fetching details for ${asset.collectionNonceId}:`, err);

          // Fallback: just get metadata
          const metadata = await fetchMetadata(asset.collection, asset.nftNonce);
          if (metadata) {
            setAssetDetails((prev) => ({
              ...prev,
              [asset.collectionNonceId]: metadata,
            }));
          }
        }
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching asset details:', error);
      setLoading(false);
    }
  };

  const listAssetsForSale = useCallback(async () => {
    setTransactionState('processing');
    setTransactionStatus('Processing transaction...');

    const listingPayloads = selectedAssets.map((asset) => {
      const assetIdentifier = `${asset.collection}/${asset.nftNonce}`;
      return {
        marketType: 0,
        marketplaceId: "417b70c0eb7a33cb",
        assetId: assetIdentifier,
        currencyId: "KLV",
        price: Number(listingPrice) * 10 ** 6,
        endTime: Math.floor(new Date(endDate).getTime() / 1000),
      };
    });

    let feePayload = null;
    let txOptions = {};
    if (useKdaFee && selectedKda) txOptions = { kdaFee: selectedKda };
    if (isTopChoice) {
      feePayload = {
        amount: 50 * 10 ** 6,
        receiver: "klv1355u5mgxl5h8gmxzz9tjt3jpp8h6frxykvvs8zya7xrd3ghhc06qxa9kyq",
        kda: selectedKda || "KLV",
      };
    }
    try {
      if (feePayload) {
        const feeTx = await web.buildTransaction([{ payload: feePayload, type: 0 }], undefined, txOptions);
        const signedFeeTx = await web.signTransaction(feeTx);
        const feeResponse = await web.broadcastTransactions([signedFeeTx]);
        if (feeResponse.error) throw new Error('Fee transaction failed');
        const feeTxHash = feeResponse.data.txsHashes[0];
        const confirmed = await waitForTransactionConfirmation(feeTxHash);
        if (!confirmed) throw new Error("Fee transaction not confirmed");
      }
      const listingTx = await web.buildTransaction(
        listingPayloads.map((payload) => ({ payload, type: 18 })),
        undefined,
        txOptions
      );
      const signedListingTx = await web.signTransaction(listingTx);
      const listingResponse = await web.broadcastTransactions([signedListingTx]);
      
      setTransactionState('success');
      setTransactionStatus("Assets listed successfully!");
      setAssets((prev) => prev.filter((asset) => !selectedAssets.includes(asset)));
      setSelectedAssets([]);
      setTimeout(closeSellModal, 2500);
    } catch (error) {
      console.error('Error in listing transactions:', error);
      setTransactionState('error');
      setTransactionStatus("Listing failed. Please try again.");
      setTimeout(() => {
        setTransactionState('');
        setTransactionStatus('');
      }, 3000);
    }
  }, [selectedAssets, listingPrice, endDate, isTopChoice, useKdaFee, selectedKda]);

  async function waitForTransactionConfirmation(txHash) {
    const maxAttempts = 10;
    const delay = 4000;
    for (let i = 0; i < maxAttempts; i++) {
      const isConfirmed = await checkTransactionStatus(txHash);
      if (isConfirmed) return true;
      await new Promise((res) => setTimeout(res, delay));
    }
    return false;
  }

  async function checkTransactionStatus(txHash) {
    try {
      const response = await axios.get(`https://api.mainnet.klever.org/v1.0/transaction/${txHash}`);
      return response.data.data.transaction.status === 'success';
    } catch (error) {
      console.error('Error checking transaction status:', error);
      return false;
    }
  }

  const sendAssets = async () => {
    if (!sendToAddress) {
      setTransactionStatus("Please enter a recipient address.");
      setTransactionState('error');
      return;
    }

    if (!sendToAddress.startsWith('klv1')) {
      setTransactionStatus("Invalid address. Klever addresses start with 'klv1'.");
      setTransactionState('error');
      return;
    }

    setTransactionState('processing');
    setTransactionStatus('Processing transfer...');

    const transferPayloads = selectedAssets.map((asset) => {
      const assetIdentifier = `${asset.collection}/${asset.nftNonce}`;
      const details = assetDetails[asset.collectionNonceId];
      const isSftAsset = details?.isSFT || asset.balance > 1;

      // For SFTs use the selected quantity, for NFTs use 1
      const amount = isSftAsset ? (sftQuantities[asset.collectionNonceId] || 1) : 1;

      return {
        receiver: sendToAddress,
        assetId: assetIdentifier,
        amount: amount,
      };
    });

    let txOptions = {};
    if (useKdaFee && selectedKda) txOptions = { kdaFee: selectedKda };

    try {
      const transferTx = await web.buildTransaction(
        transferPayloads.map((payload) => ({ payload, type: 0 })),
        undefined,
        txOptions
      );
      const signedTx = await web.signTransaction(transferTx);
      await web.broadcastTransactions([signedTx]);
      
      setTransactionState('success');
      setTransactionStatus("Assets sent successfully!");
      setAssets((prev) => prev.filter((asset) => !selectedAssets.includes(asset)));
      setSelectedAssets([]);
      setTimeout(closeSendModal, 2500);
    } catch (error) {
      console.error('Error sending assets:', error);
      setTransactionState('error');
      setTransactionStatus("Transfer failed. Please try again.");
      setTimeout(() => {
        setTransactionState('');
        setTransactionStatus('');
      }, 3000);
    }
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const yyyy = tomorrow.getFullYear();
    let mm = tomorrow.getMonth() + 1;
    let dd = tomorrow.getDate();
    mm = mm < 10 ? `0${mm}` : mm;
    dd = dd < 10 ? `0${dd}` : dd;
    return `${yyyy}-${mm}-${dd}`;
  };

  const getDateFromDays = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  const handleFixedEndDateChange = (e) => {
    const days = Number(e.target.value);
    if (days) {
      const end = new Date();
      end.setDate(end.getDate() + days);
      const yyyy = end.getFullYear();
      let mm = end.getMonth() + 1;
      let dd = end.getDate();
      mm = mm < 10 ? `0${mm}` : mm;
      dd = dd < 10 ? `0${dd}` : dd;
      setEndDate(`${yyyy}-${mm}-${dd}`);
    } else {
      setEndDate('');
    }
  };

  useEffect(() => {
    if (walletAddress) fetchAssets();
  }, [assetId, walletAddress, page]);

  const handlePaginationClick = (newPage) => {
    navigate(`/wallet/${assetId}?page=${newPage}`);
    setPage(newPage);
  };

  const PaginationButtons = ({ currentPage, totalPages }) => (
    <div className="pagination-section">
      <button
        className="pagination-btn"
        onClick={() => handlePaginationClick(Math.max(1, currentPage - 1))}
        disabled={currentPage <= 1}
      >
        <FaChevronLeft />
        <span>Previous</span>
      </button>
      <span className="page-display">
        <span className="page-current">{currentPage}</span>
        <span className="page-separator">/</span>
        <span className="page-total">{totalPages}</span>
      </span>
      <button
        className="pagination-btn"
        onClick={() => handlePaginationClick(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage >= totalPages}
      >
        <span>Next</span>
        <FaChevronRight />
      </button>
    </div>
  );

  return (
    <div className="asset-details-page">
      {/* Hero Section */}
      <div className="asset-details-hero">
        <div className="hero-content">
          <h1 className="hero-title">
            <FaLayerGroup className="hero-icon" />
            {assetId}
          </h1>
          <p className="hero-subtitle">Manage your NFT collection</p>
        </div>

        {/* Stats */}
        <div className="asset-stats-bar">
          <div className="asset-stat-card">
            <div className="stat-icon-wrapper">
              <FaImage className="stat-icon" />
            </div>
            <div className="stat-content">
              <span className="stat-label">Total NFTs</span>
              <span className="stat-value">{assets.length}</span>
            </div>
          </div>
          {selectedAssets.length > 0 && (
            <div className="asset-stat-card selected">
              <div className="stat-icon-wrapper">
                <FaCheckCircle className="stat-icon" />
              </div>
              <div className="stat-content">
                <span className="stat-label">Selected</span>
                <span className="stat-value">{selectedAssets.length}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      <div className="asset-content">
        <PaginationButtons currentPage={page} totalPages={totalPages} />

        {/* Bulk Actions */}
        <div className="bulk-actions-bar">
          <button
            onClick={toggleSelectAll}
            className={`bulk-action-btn ${selectedAssets.length === assets.length && assets.length > 0 ? 'active' : ''}`}
          >
            {selectedAssets.length === assets.length && assets.length > 0 ? (
              <>
                <FaTimesCircle />
                <span>Deselect All</span>
              </>
            ) : (
              <>
                <FaCheckCircle />
                <span>Select All</span>
              </>
            )}
          </button>

          {selectedAssets.length > 0 && (
            <>
              <button onClick={openSellModal} className="bulk-action-btn sell">
                <FaTag />
                <span>Sell ({selectedAssets.length})</span>
              </button>
              <button onClick={openSendModal} className="bulk-action-btn send">
                <FaPaperPlane />
                <span>Send ({selectedAssets.length})</span>
              </button>
            </>
          )}
        </div>

        {/* Assets Grid */}
        {loading ? (
          <div className="asset-loading">
            <FaSpinner className="loading-spinner" />
            <p className="loading-text">Loading NFTs...</p>
          </div>
        ) : assets.length === 0 ? (
          <div className="asset-empty-state">
            <FaImage className="empty-icon" />
            <h2>No NFTs Found</h2>
            <p>This collection doesn't have any NFTs on this page.</p>
          </div>
        ) : (
          <div className="asset-grid">
            {assets.map((asset, index) => {
              const details = assetDetails[asset.collectionNonceId];
              // Only mark as SFT if assetType is SemiFungible (NOT based on maxSupply)
              const isSftAsset = details?.isSFT === true;
              // Rarity only applies to SFTs, never to NFTs
              const rarity = isSftAsset ? details?.rarity : null;
              const rarityColor = rarity ? getRarityColor(rarity) : null;

              return (
                <div
                  key={asset.collectionNonceId}
                  className={`asset-nft-card ${selectedAssets.includes(asset) ? 'selected' : ''} ${isSftAsset ? 'sft-card' : ''}`}
                  onClick={() => toggleSelectAsset(asset)}
                  style={{ animationDelay: `${Math.min(index * 0.03, 1)}s` }}
                >
                  {selectedAssets.includes(asset) && (
                    <div className="selected-badge">
                      <FaCheckCircle />
                    </div>
                  )}

                  <div className="nft-card-header">
                    <span className="nft-number">
                      #{asset.nftNonce}
                      {isSftAsset && (
                        <span className="sft-badge" title="Semi-Fungible Token">
                          <FaLayerGroup />
                        </span>
                      )}
                    </span>
                    {isSftAsset && rarity && rarity !== 'Unique' && (
                      <span className="rarity-badge" style={{ color: rarityColor }}>
                        {rarity}
                      </span>
                    )}
                  </div>

                  <div className="nft-image-wrapper">
                    {details?.image ? (
                      <img
                        src={details.image}
                        alt={`${isSftAsset ? 'SFT' : 'NFT'} #${asset.nftNonce}`}
                        className="nft-image"
                      />
                    ) : (
                      <div className="nft-image-placeholder">
                        <FaImage size={40} />
                      </div>
                    )}
                  </div>

                  {/* SFT Balance Display - Only show for actual SFTs */}
                  {isSftAsset && details?.supplyInfo && (
                    <div className="sft-balance-info">
                      <FaCube className="balance-icon" />
                      <span className="balance-text">
                        Owned: <strong>{asset.balance}</strong>
                        <span className="supply-text">
                          {' '}/ {details.supplyInfo.circulating || details.supplyInfo.max} total
                        </span>
                      </span>
                    </div>
                  )}

                  {renderMetadataAttributes(details)}

                  {/* Quick List Button */}
                  <button
                    className="quick-list-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentNFT({
                        collectionId: assetId,
                        nftId: asset.nftNonce,
                        name: details?.name || `#${asset.nftNonce}`,
                        image: details?.image,
                        isSFT: isSftAsset,
                        balance: asset.balance
                      });
                      setIsAuctionModalOpen(true);
                    }}
                  >
                    <FaTag /> List for Sale
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <PaginationButtons currentPage={page} totalPages={totalPages} />
      </div>

      {/* Sell Modal - Full Page Rarible Style */}
      {isSellModalOpen && (
        <div className="fullpage-modal-overlay">
          <div className="fullpage-modal">
            {/* Left Panel - Selected Items Preview */}
            <div className="modal-preview-panel">
              <div className="preview-header">
                <h3>Selected Items</h3>
                <span className="item-count">{selectedAssets.length} items</span>
              </div>
              <div className="preview-grid">
                {selectedAssets.slice(0, 6).map(asset => {
                  const details = assetDetails[asset.collectionNonceId];
                  return (
                    <div key={asset.collectionNonceId} className="preview-item">
                      {details?.image ? (
                        <img src={details.image} alt={`#${asset.nftNonce}`} />
                      ) : (
                        <div className="preview-placeholder"><FaImage /></div>
                      )}
                      <span className="preview-nonce">#{asset.nftNonce}</span>
                    </div>
                  );
                })}
                {selectedAssets.length > 6 && (
                  <div className="preview-more">+{selectedAssets.length - 6}</div>
                )}
              </div>
              <div className="preview-collection">
                <span className="collection-label">Collection</span>
                <span className="collection-name">{assetId}</span>
              </div>
            </div>

            {/* Right Panel - Listing Form */}
            <div className="modal-form-panel">
              <div className="form-header">
                <h2><FaTag /> List for Sale</h2>
                <button
                  className="close-btn"
                  onClick={closeSellModal}
                  disabled={transactionState === 'processing'}
                >
                  <FaTimes />
                </button>
              </div>

              <div className="form-content">
                <div className="form-group">
                  <label><FaCoins /> Price per item</label>
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      placeholder="0.00"
                      value={listingPrice}
                      onChange={(e) => setListingPrice(e.target.value)}
                      disabled={transactionState === 'processing'}
                    />
                    <span className="suffix">KLV</span>
                  </div>
                  {listingPrice && selectedAssets.length > 1 && (
                    <span className="input-hint">
                      Total: {(parseFloat(listingPrice) * selectedAssets.length).toFixed(2)} KLV for {selectedAssets.length} items
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label><FaClock /> Duration</label>
                  <div className="duration-options">
                    {[1, 7, 14, 30].map(days => (
                      <button
                        key={days}
                        type="button"
                        className={`duration-btn ${endDate === getDateFromDays(days) ? 'active' : ''}`}
                        onClick={() => {
                          const date = new Date();
                          date.setDate(date.getDate() + days);
                          setEndDate(date.toISOString().split('T')[0]);
                        }}
                        disabled={transactionState === 'processing'}
                      >
                        {days}d
                      </button>
                    ))}
                  </div>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={getTomorrowDate()}
                    className="date-input"
                    disabled={transactionState === 'processing'}
                  />
                </div>

                <div className="form-group featured">
                  <label className="toggle-row">
                    <div className="toggle-info">
                      <span className="toggle-title">⭐ Top Choice</span>
                      <span className="toggle-desc">Feature your listing (+50 KLV)</span>
                    </div>
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={isTopChoice}
                        onChange={(e) => setIsTopChoice(e.target.checked)}
                        disabled={transactionState === 'processing'}
                      />
                      <span className="toggle-slider"></span>
                    </div>
                  </label>
                </div>

                <div className="fee-breakdown">
                  <div className="fee-row">
                    <span>Network Fee</span>
                    <span>{useKdaFee ? 'Paid with KDA' : '~2 KLV'}</span>
                  </div>
                  {isTopChoice && (
                    <div className="fee-row highlight">
                      <span>Top Choice Fee</span>
                      <span>50 KLV</span>
                    </div>
                  )}
                  <div className="fee-row total">
                    <span>Total Fees</span>
                    <span>{isTopChoice ? (useKdaFee ? '50 KLV' : '~52 KLV') : (useKdaFee ? '0 KLV' : '~2 KLV')}</span>
                  </div>
                </div>

                {transactionStatus && (
                  <div className={`status-banner ${transactionState}`}>
                    {transactionState === 'processing' && <FaSpinner className="spin" />}
                    {transactionState === 'success' && <FaCheckCircle />}
                    {transactionState === 'error' && <FaTimesCircle />}
                    <span>{transactionStatus}</span>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button
                  className="btn-secondary"
                  onClick={closeSellModal}
                  disabled={transactionState === 'processing'}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={listAssetsForSale}
                  disabled={!listingPrice || !endDate || transactionState === 'processing'}
                >
                  {transactionState === 'processing' ? (
                    <><FaSpinner className="spin" /> Listing...</>
                  ) : (
                    <><FaTag /> List {selectedAssets.length} Item{selectedAssets.length > 1 ? 's' : ''}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Modal - Full Page Rarible Style */}
      {isSendModalOpen && (
        <div className="fullpage-modal-overlay">
          <div className="fullpage-modal">
            {/* Left Panel - Selected Items Preview */}
            <div className="modal-preview-panel">
              <div className="preview-header">
                <h3>Sending</h3>
                <span className="item-count">{selectedAssets.length} items</span>
              </div>
              <div className="preview-grid">
                {selectedAssets.slice(0, 6).map(asset => {
                  const details = assetDetails[asset.collectionNonceId];
                  return (
                    <div key={asset.collectionNonceId} className="preview-item">
                      {details?.image ? (
                        <img src={details.image} alt={`#${asset.nftNonce}`} />
                      ) : (
                        <div className="preview-placeholder"><FaImage /></div>
                      )}
                      <span className="preview-nonce">#{asset.nftNonce}</span>
                    </div>
                  );
                })}
                {selectedAssets.length > 6 && (
                  <div className="preview-more">+{selectedAssets.length - 6}</div>
                )}
              </div>
              <div className="preview-collection">
                <span className="collection-label">Collection</span>
                <span className="collection-name">{assetId}</span>
              </div>
            </div>

            {/* Right Panel - Send Form */}
            <div className="modal-form-panel">
              <div className="form-header">
                <h2><FaPaperPlane /> Send Items</h2>
                <button
                  className="close-btn"
                  onClick={closeSendModal}
                  disabled={transactionState === 'processing'}
                >
                  <FaTimes />
                </button>
              </div>

              <div className="form-content">
                <div className="form-group">
                  <label>Recipient Address</label>
                  <input
                    type="text"
                    placeholder="klv1..."
                    value={sendToAddress}
                    onChange={(e) => setSendToAddress(e.target.value)}
                    className="address-input"
                    disabled={transactionState === 'processing'}
                  />
                  {sendToAddress && !sendToAddress.startsWith('klv1') && (
                    <span className="input-error">Address must start with klv1</span>
                  )}
                </div>

                {/* SFT Quantity Selectors */}
                {selectedAssets.some(asset => {
                  const details = assetDetails[asset.collectionNonceId];
                  return details?.isSFT || asset.balance > 1;
                }) && (
                  <div className="form-group">
                    <label>Quantities</label>
                    <div className="quantity-list">
                      {selectedAssets.map(asset => {
                        const details = assetDetails[asset.collectionNonceId];
                        const isSftAsset = details?.isSFT || asset.balance > 1;
                        if (!isSftAsset) return null;

                        const maxQty = asset.balance || 1;
                        const currentQty = sftQuantities[asset.collectionNonceId] || 1;

                        return (
                          <div key={asset.collectionNonceId} className="quantity-row">
                            <div className="quantity-item-info">
                              <span className="item-nonce">#{asset.nftNonce}</span>
                              <span className="item-max">max: {maxQty}</span>
                            </div>
                            <div className="quantity-controls">
                              <button
                                type="button"
                                onClick={() => {
                                  const newQty = Math.max(1, currentQty - 1);
                                  setSftQuantities(prev => ({
                                    ...prev,
                                    [asset.collectionNonceId]: newQty
                                  }));
                                }}
                                disabled={currentQty <= 1 || transactionState === 'processing'}
                              >
                                <FaMinus />
                              </button>
                              <input
                                type="number"
                                value={currentQty}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 1;
                                  const newQty = Math.max(1, Math.min(maxQty, val));
                                  setSftQuantities(prev => ({
                                    ...prev,
                                    [asset.collectionNonceId]: newQty
                                  }));
                                }}
                                min="1"
                                max={maxQty}
                                disabled={transactionState === 'processing'}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const newQty = Math.min(maxQty, currentQty + 1);
                                  setSftQuantities(prev => ({
                                    ...prev,
                                    [asset.collectionNonceId]: newQty
                                  }));
                                }}
                                disabled={currentQty >= maxQty || transactionState === 'processing'}
                              >
                                <FaPlus />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="fee-breakdown">
                  <div className="fee-row">
                    <span>Network Fee</span>
                    <span>{useKdaFee ? 'Paid with KDA' : '~2 KLV'}</span>
                  </div>
                </div>

                {transactionStatus && (
                  <div className={`status-banner ${transactionState}`}>
                    {transactionState === 'processing' && <FaSpinner className="spin" />}
                    {transactionState === 'success' && <FaCheckCircle />}
                    {transactionState === 'error' && <FaTimesCircle />}
                    <span>{transactionStatus}</span>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button
                  className="btn-secondary"
                  onClick={closeSendModal}
                  disabled={transactionState === 'processing'}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={sendAssets}
                  disabled={!sendToAddress || !sendToAddress.startsWith('klv1') || transactionState === 'processing'}
                >
                  {transactionState === 'processing' ? (
                    <><FaSpinner className="spin" /> Sending...</>
                  ) : (
                    <><FaPaperPlane /> Send {selectedAssets.length} Item{selectedAssets.length > 1 ? 's' : ''}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auction Listing Modal */}
      {isAuctionModalOpen && currentNFT && (
        <AuctionListingModal
          nft={currentNFT}
          onClose={() => {
            setIsAuctionModalOpen(false);
            setCurrentNFT(null);
          }}
          onSuccess={() => {
            setIsAuctionModalOpen(false);
            setCurrentNFT(null);
            fetchAssets();
          }}
        />
      )}
    </div>
  );
};

export default AssetDetailsPage;